import React, { useEffect } from "react";
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  Tooltip,
  Checkbox,
  FormControlLabel,
  OutlinedInput,
  Divider,
  Box,
  SxProps,
  Theme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import uuid from "react-uuid";

import { withSnackbar } from "../../utils/common/SnackbarHoc";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "../../utils/common/common";
import { cloneDeep, unescape } from "lodash";

interface IPreAISettingsProps {
  name: string;
  accordionSettings: any;
  handleAccordionChange: (name: string) => void;
  snackbarShowMessage: any;
  aiSettings: any;
  setAISettings: (values: any) => void;
  sx: SxProps<Theme> | undefined;
}

const PreAISettings = ({
  accordionSettings,
  handleAccordionChange,
  aiSettings,
  setAISettings,
  sx,
}: IPreAISettingsProps) => {
  const handleInputChange = (e: any, id: string) => {
    const { name, value } = e.target;
    setAISettings((prevState: any) => {
      const clonedAISettings = cloneDeep(prevState);
      clonedAISettings.intelligenceLayer =
        clonedAISettings.intelligenceLayer.map((item: any) => {
          if (item.id !== id) return item;

          return { ...item, [name]: value };
        });

      return clonedAISettings;
    });
  };

  const handleCheckboxChange = (e: any, id: string) => {
    const { name, checked } = e.target;
    setAISettings((prevState: any) => {
      const clonedAISettings = cloneDeep(prevState);
      clonedAISettings.intelligenceLayer =
        clonedAISettings.intelligenceLayer.map((item: any) => {
          if (item.id !== id) return item;

          return { ...item, [name]: checked };
        });
      return clonedAISettings;
    });
  };

  const addTemplate = () => {
    setAISettings((prevState: any) => {
      const clonedAISettings = cloneDeep(prevState);
      clonedAISettings.intelligenceLayer = [
        ...clonedAISettings.intelligenceLayer,
        { id: uuid(), question: "", answer: "", isAdditionalKnowledge: false },
      ];
      return clonedAISettings;
    });
  };

  const deleteTemplate = (id: string) => {
    setAISettings((prevState: any) => {
      const clonedAISettings = cloneDeep(prevState);
      clonedAISettings.intelligenceLayer =
        clonedAISettings.intelligenceLayer.filter(
          (item: any) => item.id !== id
        );
      return clonedAISettings;
    });
  };

  const isAnyQuestionMissingId = aiSettings.intelligenceLayer.some(
    (i: any) => !i.id
  );

  useEffect(() => {
    if (!isAnyQuestionMissingId) return;

    setAISettings((prevState: any) => {
      const clonedAISettings = cloneDeep(prevState);
      clonedAISettings.intelligenceLayer =
        clonedAISettings.intelligenceLayer.map((item: any) => {
          if (item.id) return item;

          return { ...item, id: uuid() };
        });
      return clonedAISettings;
    });
  }, [isAnyQuestionMissingId]);

  return (
    <Box className="accordion-wrap" sx={sx}>
      <Accordion
        expanded={accordionSettings["preAISettings"]}
        onChange={() => handleAccordionChange("preAISettings")}
      >
        <AccordionSummary aria-controls={"panel1-content"} id={"panel1-header"}>
          <Grid container className="acc-prod-icon">
            <Grid item className="acc-prod-icon">
              Pre AI Settings
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {aiSettings?.intelligenceLayer?.map((item: any, index: number) => {
              return (
                <Grid
                  container
                  spacing={3}
                  sx={{ alignItems: "center", mb: 3, pl: "16px" }}
                  key={item.id + index}
                >
                  <Grid item xs={11} sx={{ flexGrow: 1, mt: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControl className="custom-select" fullWidth>
                          <InputLabel>
                            Custom Question
                            <div
                              style={{
                                display: "inline-block",
                                color: "red",
                              }}
                            >
                              *
                            </div>{" "}
                          </InputLabel>

                          <OutlinedInput
                            type="text"
                            id="question-select"
                            label="Question"
                            name="question"
                            value={unescape(item.question) || ""}
                            onChange={(e) => handleInputChange(e, item.id)}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl className="custom-select" fullWidth>
                          <InputLabel>
                            Custom Answer
                            <div
                              style={{
                                display: "inline-block",
                                color: "red",
                              }}
                            >
                              *
                            </div>{" "}
                          </InputLabel>

                          <OutlinedInput
                            type="text"
                            id="answer-select"
                            label="Answer"
                            name="answer"
                            multiline
                            value={unescape(item.answer) || ""}
                            onChange={(e) => handleInputChange(e, item.id)}
                          />
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        {" "}
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="isAdditionalKnowledge"
                              onChange={(e) => handleCheckboxChange(e, item.id)}
                              checked={
                                item.isAdditionalKnowledge ? true : false
                              }
                              color="primary"
                            />
                          }
                          label="Append this answer to the response from knowledgebase"
                        />
                      </Grid>
                      {!(aiSettings.intelligenceLayer.length - 1 == index) && (
                        <Grid item xs={12}>
                          <Divider style={{ borderStyle: "solid" }}></Divider>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                  <Grid item>
                    <>
                      <Tooltip
                        title="Delete Custom Question and Answer"
                        placement="top"
                      >
                        <CloseIcon
                          className="cursor-pointer"
                          onClick={() => deleteTemplate(item.id)}
                        />
                      </Tooltip>
                    </>
                  </Grid>
                </Grid>
              );
            })}
            <Grid item xs={12}>
              <Button
                fullWidth
                type="button"
                variant="contained"
                onClick={addTemplate}
                sx={{
                  textTransform: "uppercase",
                }}
              >
                <AddIcon />
                Add a Question and Answer
              </Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default withSnackbar(PreAISettings);
